/**
 * Admin "Contract" card: hide billing from members and set a no-payment monthly Boost top-up.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, FileSignature, Loader2 } from "lucide-react";
import {
  useAdminWorkspaceContract,
  useAdminUpdateWorkspaceContract,
} from "../../hooks/useAdminWorkspaces";
import {
  type ContractForm,
  formFromContract,
  toContractUpdate,
  withBillingHidden,
} from "../../lib/contractForm";

interface WorkspaceContractCardProps {
  workspaceId: string;
}

interface ToggleProps {
  label: string;
  help?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

function Toggle({ label, help, checked, disabled, onChange }: ToggleProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {help && <p className="text-xs text-muted-foreground">{help}</p>}
      </div>
      <label className="relative inline-flex items-center cursor-pointer shrink-0">
        <input
          type="checkbox"
          role="switch"
          aria-label={label}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border dark:after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50" />
      </label>
    </div>
  );
}

export function WorkspaceContractCard({ workspaceId }: WorkspaceContractCardProps) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useAdminWorkspaceContract(workspaceId);
  const saveMutation = useAdminUpdateWorkspaceContract(workspaceId);
  // null = no edit in progress: the form follows what the server holds.
  const [draft, setDraft] = useState<ContractForm | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const form = draft ?? (data ? formFromContract(data) : null);

  function update(patch: Partial<ContractForm>) {
    if (form) setDraft({ ...form, ...patch });
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    saveMutation.mutate(toContractUpdate(form), {
      onSuccess: (result) => {
        setDraft(null);
        setSaved(t(result.applied ? "adminWorkspaces.detail.contract.savedApplied" : "adminWorkspaces.detail.contract.saved"));
        setTimeout(() => setSaved(null), 5000);
      },
    });
  }

  const nextTopUp = data?.nextTopUpAt
    ? new Date(data.nextTopUpAt).toLocaleDateString(i18n.language, {
        timeZone: "America/Toronto",
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <div className="p-4 border border-border rounded-lg">
      <div className="flex items-center gap-2 mb-3">
        <FileSignature className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">{t("adminWorkspaces.detail.contract.title")}</h3>
      </div>

      {isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      ) : error ? (
        <p className="text-sm text-red-600">{error.message}</p>
      ) : form ? (
        <form onSubmit={handleSave} className="space-y-4">
          <Toggle
            label={t("adminWorkspaces.detail.contract.hideBilling")}
            help={t("adminWorkspaces.detail.contract.hideBillingHelp")}
            checked={form.billingHidden}
            onChange={(on) => setDraft(withBillingHidden(form, on))}
          />
          <Toggle
            label={t("adminWorkspaces.detail.contract.monthlyTopUp")}
            checked={form.topUpEnabled}
            onChange={(on) => update({ topUpEnabled: on })}
          />
          {form.topUpEnabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  {t("adminWorkspaces.detail.contract.amount")}
                </label>
                <input
                  type="number"
                  value={form.topUpAmount}
                  onChange={(e) => update({ topUpAmount: e.target.value })}
                  min={1}
                  max={100000}
                  step={1}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  {t("adminWorkspaces.detail.contract.startDate")}
                </label>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => update({ startDate: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                  required
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("adminWorkspaces.detail.contract.startDateHelp")}
                </p>
              </div>
            </div>
          )}
          <Toggle
            label={t("adminWorkspaces.detail.contract.expireRemaining")}
            help={t("adminWorkspaces.detail.contract.expireRemainingHelp")}
            checked={form.expireRemaining}
            disabled={!form.billingHidden}
            onChange={(on) => update({ expireRemaining: on })}
          />
          {nextTopUp && (
            <p className="text-sm text-muted-foreground">
              {t("adminWorkspaces.detail.contract.nextTopUp", { date: nextTopUp })}
            </p>
          )}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 rounded-lg transition-colors"
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("common.save")}
            </button>
            {saved && (
              <span className="inline-flex items-center gap-1 text-sm text-green-600">
                <Check className="h-4 w-4" />
                {saved}
              </span>
            )}
            {saveMutation.isError && (
              <span className="text-sm text-red-600">{saveMutation.error.message}</span>
            )}
          </div>
        </form>
      ) : null}
    </div>
  );
}
